import { useCallback, useEffect, useState } from 'react';

import { useCountryCode } from '../../../hooks/useCountryCode';
import { useCurrency } from '../../../hooks/useCurrency';
import { useAuth } from '../../../providers';
import { usePurchase } from '../../../providers/PurchaseProvider';
import { CountryCode } from '../../../types/Account';
import {
  GeneratedAddress,
  GeneratedCreateDrawEntryIntentMutation,
  GeneratedDrawType,
  GeneratedOrderItem,
  GeneratedPaymentMethod,
} from '../../../types/generated';
import { CurrencyCode } from '../../../types/Locations';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { captureError } from '../../../utils/capture';
import { shopifyGraphqlIdToAdminId } from '../../../utils/shopifyIds';
import { sortIso8601 } from '../../../utils/sort';
import {
  DrawByProductId,
  PaymentType,
  PreAuthDrawController,
  PreAuthDrawOptions,
  StripeAddressDetails,
} from '../types';

export function usePreAuthDraw(
  options: PreAuthDrawOptions,
): PreAuthDrawController {
  const { user } = useAuth();
  const { accountGateway, drawGateway, paymentGateway, product } = options;
  const purchasingController = usePurchase();

  const [countryCode, setCountry] = useCountryCode();
  const [frameEmitter] = useAnalyticsEmitter();
  const purchasingProduct = purchasingController.product;
  const { shopifyId } = purchasingProduct ?? { shopifyId: '' };

  const [setupComplete, setSetupComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [draw, setDraw] = useState<DrawByProductId | undefined>(undefined);

  const [currencyCode] = useCurrency();
  const currency =
    draw?.drawType === GeneratedDrawType.Auction && draw.auctionReservePrice
      ? draw.auctionReservePrice.currencyCode
      : currencyCode;

  const [setupSecret, setSetupSecret] = useState<string>();
  const [pricing, setPricing] =
    useState<
      NonNullable<
        Required<GeneratedCreateDrawEntryIntentMutation>['createDrawEntryIntent']
      >['pricing']
    >();
  const [readyToConfirm, setReadyToConfirm] = useState(false);
  const [cryptoComPaymentId, setCryptoComPaymentId] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string | true>();

  const [selectedAddress, setSelectedAddress] = useState<string>();
  const [addresses, setAddresses] = useState<GeneratedAddress[]>([]);
  const [bid, setBid] = useState<number>();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);
  const [paymentMethods, setPaymentMethods] = useState<
    GeneratedPaymentMethod[]
  >([]);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<PaymentType | null>(null);

  const [optionalItems, setOptionalItems] = useState<GeneratedOrderItem[]>([]);

  const [drawEntryIntent, setDrawEntryIntent] =
    useState<
      NonNullable<
        GeneratedCreateDrawEntryIntentMutation['createDrawEntryIntent']
      >['intent']
    >();

  const [billingAddress, setBillingAddress] =
    useState<StripeAddressDetails | null>(null);
  const [usingNewPaymentMethod, setUsingNewPaymentMethod] = useState(false);
  const [newPaymentMethodType, setNewPaymentMethodType] = useState<string>();
  const [paymentErrorCode, setPaymentErrorCode] = useState<string | null>(null);
  const [
    newPaymentMethodUsingShippingAddress,
    setNewPaymentMethodUsingShippingAddress,
  ] = useState(true);

  async function fetchAddresses(): Promise<string | null> {
    const addressesResponse = await accountGateway.getAddresses();

    if (!Array.isArray(addressesResponse)) return null;

    const addressesWithPhoneNumber = addressesResponse.filter((address) =>
      Boolean(address.phone),
    );

    setAddresses(addressesWithPhoneNumber);

    const defaultAddress = addressesWithPhoneNumber.find((a) => a.isDefault);

    if (defaultAddress) {
      setSelectedAddress(defaultAddress.id);

      return defaultAddress.id;
    }

    const latestAddress = addressesWithPhoneNumber.sort(
      sortIso8601((a) => a.updatedAt || a.createdAt || '2020-01-01T00:00:00Z'),
    )[0];

    if (latestAddress) setSelectedAddress(latestAddress.id);

    return null;
  }

  async function fetchPaymentMethods(): Promise<string | null> {
    const paymentMethodsResponse = await paymentGateway.listPaymentMethods();

    if (!Array.isArray(paymentMethodsResponse)) return null;

    setPaymentMethods(paymentMethodsResponse);

    const defaultPm = paymentMethodsResponse.find((p) => p.default);

    if (defaultPm) {
      setSelectedPaymentMethodId(defaultPm.providerPaymentMethodId);
      setSelectedPaymentType(PaymentType.CARD);

      return defaultPm.providerPaymentMethodId;
    }

    const firstPaymentMethod = paymentMethodsResponse[0];

    if (firstPaymentMethod) {
      setSelectedPaymentMethodId(firstPaymentMethod.providerPaymentMethodId);
      setSelectedPaymentType(PaymentType.CARD);
    }

    return null;
  }

  async function fetchDraw(): Promise<DrawByProductId> {
    const drawResponse = await drawGateway.getDrawByProductId(
      shopifyGraphqlIdToAdminId(shopifyId),
    );

    if (!drawResponse) {
      throw new Error(
        `Unable to fetch draw by product id shopify_product_id:${shopifyId}`,
      );
    }

    setDraw(drawResponse);

    return drawResponse;
  }

  async function setupController() {
    setLoading(true);

    try {
      const defaultOptionalItems: GeneratedOrderItem[] = [];
      const [defaultShippingAddressId, defaultPaymentMethodId, drawResponse] =
        await Promise.all([
          fetchAddresses(),
          fetchPaymentMethods(),
          fetchDraw(),
        ]);

      // Check frame on purchasing controller
      const { frame } = purchasingController;
      if (frame) {
        defaultOptionalItems.push({
          productID: shopifyGraphqlIdToAdminId(
            frame.variant.consolidatedVariants[0].shopifyFrameId,
          ),
          variantID: shopifyGraphqlIdToAdminId(
            frame.variant.consolidatedVariants[0].shopifyFrameVariantId,
          ),
          quantity: 1,
        });
      }

      setOptionalItems(defaultOptionalItems);
      setDraw(drawResponse);

      // Create draw entry intent
      const createDeiResponse = await drawGateway.createDrawEntryIntent(
        drawResponse.id,
        currency as CurrencyCode,
        defaultShippingAddressId ?? null,
        defaultPaymentMethodId ?? null,
        defaultOptionalItems,
        null,
      );

      if (!createDeiResponse) {
        throw new Error(
          `Unable to create draw entry intent draw_id:${drawResponse.id}`,
        );
      }

      // Update state
      setDrawEntryIntent(createDeiResponse.intent);
      setPricing(createDeiResponse.pricing);
      setReadyToConfirm(false);
      setSetupSecret(createDeiResponse.setupSecret);

      // lazyboi
      setSetupComplete(true);
      setErrorMessage(void 0);
    } catch (error) {
      if (error instanceof Error) {
        captureError(error);
      }

      setErrorMessage(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleDiscardedDrawEntryIntent = useCallback(async () => {
    console.warn('Discarded draw entry intent detected, creating new.');

    try {
      // Create a new draw entry intent using existing data
      const createDeiResponse = await drawGateway.createDrawEntryIntent(
        draw!.id,
        currency as CurrencyCode,
        selectedAddress,
        selectedPaymentMethodId,
        optionalItems,
        selectedPaymentType === null
          ? null
          : selectedPaymentType === PaymentType.CRYPTO,
      );

      if (!createDeiResponse) {
        throw new Error(
          `Unable to create draw entry intent draw_id:${draw!.id}`,
        );
      }

      // Update state
      setPricing(createDeiResponse.pricing);
      setDrawEntryIntent(createDeiResponse.intent);
      setSetupSecret(createDeiResponse.setupSecret);
      setSetupComplete(true);
      setErrorMessage(void 0);
    } catch (error) {
      setErrorMessage(true);
      console.error(error);
    }
  }, [
    currency,
    draw,
    drawGateway,
    optionalItems,
    selectedAddress,
    selectedPaymentMethodId,
    selectedPaymentType,
  ]);

  const updateDrawEntryIntent = useCallback(async () => {
    if (
      !user ||
      (product.releaseType !== 'draw-release' &&
        product.releaseType !== 'auction') ||
      !draw ||
      !drawEntryIntent?.id ||
      !selectedAddress
    )
      return;

    setUpdating(true);

    try {
      const response = await drawGateway.updateDrawEntryIntent(
        drawEntryIntent.id,
        currency as CurrencyCode,
        selectedAddress ?? null,
        selectedPaymentMethodId ?? null,
        optionalItems,
        selectedPaymentType === null
          ? null
          : selectedPaymentType === PaymentType.CRYPTO,
        bid
          ? {
              amount: bid?.toString(),
              currencyCode: currency,
            }
          : undefined,
      );

      if (!response) {
        throw new Error(
          `Unable to update draw entry intent draw_entry_intent:${drawEntryIntent.id}`,
        );
      }

      setPricing(response.pricing);
      setDrawEntryIntent(response.intent);
      setReadyToConfirm(response.readyToConfirm);
      setCryptoComPaymentId(response.cryptoComPaymentId || undefined);
      setErrorMessage(void 0);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === 'draw entry intent has been discarded'
      ) {
        await handleDiscardedDrawEntryIntent();

        return;
      }

      setErrorMessage(true);
      console.error(error);
    } finally {
      setUpdating(false);
    }
  }, [
    bid,
    currency,
    draw,
    drawEntryIntent?.id,
    drawGateway,
    handleDiscardedDrawEntryIntent,
    optionalItems,
    product.releaseType,
    selectedAddress,
    selectedPaymentMethodId,
    selectedPaymentType,
    user,
  ]);

  const handleUpdateAddress = useCallback(
    (addressId: string) => {
      const selectedAddressDetails = (addresses || []).find((address) => {
        return address.id === addressId;
      });
      if (
        selectedAddressDetails &&
        selectedAddressDetails.countryCode &&
        selectedAddressDetails.countryCode !== countryCode
      ) {
        setCountry(selectedAddressDetails.countryCode as CountryCode);
      }
    },
    [addresses, countryCode, setCountry],
  );

  async function refreshAddresses(newSelectedAddressId: string | undefined) {
    const addressesResponse = await accountGateway.getAddresses();

    if (!Array.isArray(addressesResponse)) return;

    setAddresses(addressesResponse);

    if (newSelectedAddressId !== void 0) {
      setSelectedAddress(newSelectedAddressId);

      return;
    }

    const defaultAddress = addressesResponse.find((a) => a.isDefault);

    if (defaultAddress) setSelectedAddress(defaultAddress.id);
  }

  useEffect(() => {
    if (!setupComplete) return;

    updateDrawEntryIntent();
  }, [
    setupComplete,
    selectedAddress,
    selectedPaymentMethodId,
    selectedPaymentType,
    updateDrawEntryIntent,
  ]);

  useEffect(() => {
    const { frame } = purchasingController;

    if (frame) {
      setOptionalItems([
        {
          productID: shopifyGraphqlIdToAdminId(
            frame.variant.consolidatedVariants[0].shopifyFrameId,
          ),
          variantID: shopifyGraphqlIdToAdminId(
            frame.variant.consolidatedVariants[0].shopifyFrameVariantId,
          ),
          quantity: 1,
        },
      ]);

      frameEmitter('frame', 'selected', {
        'frame name': frame.title,
        'frame price': frame.variant.consolidatedVariants[0].price,
      });
    } else {
      setOptionalItems([]);
    }
  }, [frameEmitter, purchasingController, purchasingController.frame]);

  useEffect(() => {
    setSetupComplete(false);
  }, [shopifyId]);

  useEffect(() => {
    if (selectedAddress) handleUpdateAddress(selectedAddress);
  }, [handleUpdateAddress, selectedAddress]);

  return {
    loading,
    bid,
    setBid,
    updating,
    draw,
    setupController,
    setupSecret,
    updateDrawEntryIntent,
    entryIntentResponse: {
      intent: drawEntryIntent,
      pricing,
      readyToConfirm,
      cryptoComPaymentId,
    },
    setupComplete,

    errorMessage,

    setSelectedAddress,
    selectedAddress,
    addresses,
    refreshAddresses,

    setSelectedPaymentMethodId,
    selectedPaymentMethodId,
    paymentMethods,

    selectedPaymentType,
    setSelectedPaymentType,

    usingNewPaymentMethod,
    newPaymentMethodType,
    paymentErrorCode,
    setPaymentErrorCode,
    setNewPaymentMethodType,
    newPaymentMethodUsingShippingAddress,
    setUsingNewPaymentMethod,
    setNewPaymentMethodUsingShippingAddress,
    billingAddress,
    setBillingAddress,

    optionalItems,
    setOptionalItems,
  };
}
